/**
 * The API tab's response (design "API tab", right): status, time, size, "Copy as curl" (secrets masked), then Body
 * (pretty JSON), Headers, Logs (the service's lines printed during the call) and Data (to the project's Data tab).
 * When Flow cannot sign the chosen app user in by itself, it asks for the password here, once.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Copy, Check, Database } from '@phosphor-icons/react';
import { formatSize, prettyBody, statusName, statusTone } from '../../utils/apiTesterModel';
import { Loading } from '../data/DataCommon';

const TOKEN = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;
const HIGHLIGHT_MAX = 200000;

/** Pretty JSON with keys, strings and numbers coloured. */
export function JsonView({ text }) {
  if (text.length > HIGHLIGHT_MAX) { return <pre className="api-pre">{text}</pre>; }
  const pieces = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    if (match.index > last) { pieces.push(text.slice(last, match.index)); }
    const [whole, string, colon, literal, number] = match;
    const kind = string ? (colon ? 'k' : 's') : literal ? 'n' : number ? 'n' : '';
    pieces.push(<span className={`api-j-${kind}`} key={match.index}>{string || whole}</span>);
    if (colon) { pieces.push(colon); }
    last = match.index + whole.length;
  }
  pieces.push(text.slice(last));
  return <pre className="api-pre">{pieces}</pre>;
}

JsonView.propTypes = { text: PropTypes.string.isRequired };

export function SignInPrompt({ user, busy, error, onSignIn, onCancel }) {
  const [password, setPassword] = useState('');
  return (
    <form className="api-signin" data-testid="api-signin" onSubmit={(event) => { event.preventDefault(); if (password) { onSignIn(password); } }}>
      <strong>Sign in as {user.name || user.email}</strong>
      <p className="data-muted">Flow does not know this user&apos;s password. Enter it once: Flow signs in through the app&apos;s own login and keeps only the session, on the server.</p>
      <div className="data-row-gap is-wrap">
        <label className="data-sr" htmlFor="api-signin-password">Password of {user.email}</label>
        <input autoComplete="off" className="data-input" data-autofocus id="api-signin-password" onChange={(event) => setPassword(event.target.value)} placeholder="Password" type="password" value={password} />
        <button className="data-btn is-primary" disabled={busy || !password} type="submit">{busy ? 'Signing in…' : 'Sign in and send'}</button>
        <button className="data-btn is-ghost" onClick={onCancel} type="button">Cancel</button>
      </div>
      {error && <p className="data-error-text" role="alert">{error}</p>}
    </form>
  );
}

SignInPrompt.propTypes = {
  busy: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};

export default function ResponseCard({ answer, error, sending, onCopyCurl, onOpenData, signIn = null }) {
  const [part, setPart] = useState('body');
  const [copied, setCopied] = useState(false);
  const copy = async() => {
    const ok = await onCopyCurl();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  const body = prettyBody(answer);
  const parts = [
    { id: 'body', label: 'Body' },
    { id: 'headers', label: `Headers${answer ? ` ${answer.headers.length}` : ''}` },
    { id: 'logs', label: `Logs${answer ? ` ${answer.logs.length}` : ''}` },
    { id: 'data', label: 'Data' },
  ];
  return (
    <section aria-labelledby="api-res-title" className="api-card api-half" data-testid="api-response">
      <div className="api-res-head">
        <h2 className="api-res-title" id="api-res-title">Response</h2>
        {answer && (
          <>
            <span className={`data-pill is-${statusTone(answer.status)}`} data-testid="api-status">{answer.status} {statusName(answer.status, answer.statusText)}</span>
            <span className="data-pill">{answer.ms} ms</span>
            <span className="data-pill">{formatSize(answer.size)}{answer.truncated ? ' (cut at 1 MB)' : ''}</span>
          </>
        )}
        <span className="data-grow" />
        <button className="data-btn" onClick={copy} type="button">{copied ? <Check aria-hidden size={14} /> : <Copy aria-hidden size={14} />}{copied ? 'Copied' : 'Copy as curl'}</button>
      </div>
      {signIn}
      {!signIn && sending && <Loading label="Sending…" />}
      {!signIn && !sending && error && <p className="api-error" role="alert">{error}</p>}
      {!signIn && !sending && !error && !answer && <p className="api-empty">Pick an endpoint on the left, or write a path, then Send.</p>}
      {!signIn && !sending && answer && (
        <>
          <div aria-label="Response parts" className="api-sub" role="tablist">
            {parts.map((p) => (
              <button aria-selected={part === p.id} className={part === p.id ? 'is-on' : ''} key={p.id} onClick={() => setPart(p.id)} role="tab" type="button">{p.label}</button>
            ))}
          </div>
          <div className="api-part" role="tabpanel">
            {part === 'body' && (body.kind === 'json' ? <JsonView text={body.text} /> : body.kind === 'binary' ? <p className="data-muted">{body.text}</p> : <pre className="api-pre">{body.text || '(empty)'}</pre>)}
            {part === 'headers' && (
              <table className="api-kv"><tbody>{answer.headers.map(([name, value], index) => <tr key={`${name}-${index}`}><th className="data-mono">{name}</th><td className="data-mono">{value}</td></tr>)}</tbody></table>
            )}
            {part === 'logs' && (answer.logs.length
              ? <pre className="api-pre api-logs" data-testid="api-logs">{answer.logs.map((l) => `${l.type === 'stderr' ? '! ' : ''}${l.line}`).join('\n')}</pre>
              : <p className="data-muted">The service printed nothing during this call.</p>)}
            {part === 'data' && (
              <div className="api-data">
                {onOpenData ? (
                  <>
                    <p className="data-muted">What the call changed lives in the project&apos;s stores: open them in Data to look.</p>
                    <button className="data-btn" onClick={onOpenData} type="button"><Database aria-hidden size={14} />Open Data</button>
                  </>
                ) : <p className="data-muted">What the call changed lives in the project&apos;s stores.</p>}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

ResponseCard.propTypes = {
  answer: PropTypes.object,
  error: PropTypes.string,
  onCopyCurl: PropTypes.func.isRequired,
  // Null when the person's plan has no Data tab: no Open Data button.
  onOpenData: PropTypes.func,
  sending: PropTypes.bool,
  signIn: PropTypes.node,
};
